/**
 * 支出入力フォームのロジック。
 *
 * 「入力欄の状態 → API へ送る形」の変換と、入力候補を選んだときの
 * フォームへの反映を、UI から独立した純粋関数として定義する。
 * Web でもネイティブでも同じ挙動になり、テストも書きやすい。
 */
import { allocate, inferCategory, parseAmountInput, } from "@spli/core";
export function createEmptyForm(members, today) {
    return {
        title: "",
        category: "other",
        amountText: "",
        payerId: members[0]?.id ?? null,
        participantIds: members.map((m) => m.id),
        splitMode: "equal",
        splitValues: {},
        occurredOn: today,
        note: "",
    };
}
/** 既存の支出を編集するためのフォーム状態に変換する */
export function toFormState(expense, currency) {
    const splitValues = {};
    let splitMode = "equal";
    let participantIds = [];
    switch (expense.split.type) {
        case "equal":
            participantIds = expense.split.memberIds;
            break;
        case "shares":
            splitMode = "shares";
            participantIds = expense.split.entries.map((e) => e.memberId);
            for (const e of expense.split.entries)
                splitValues[e.memberId] = String(e.share);
            break;
        case "percentage":
            // パーセント指定は比率として編集する (合計 100 の比率と等価)
            splitMode = "shares";
            participantIds = expense.split.entries.map((e) => e.memberId);
            for (const e of expense.split.entries)
                splitValues[e.memberId] = String(e.percent);
            break;
        case "exact":
            // 全員分の金額指定は「全員を調整した adjust」と同じ意味になる
            splitMode = "adjust";
            participantIds = expense.split.entries.map((e) => e.memberId);
            for (const e of expense.split.entries) {
                splitValues[e.memberId] = formatAmountForInput(e.amount, currency);
            }
            break;
        case "adjusted":
            // 金額を固定していた人だけ入力値を戻し、残りは空欄 (均等割り) のままにする
            splitMode = "adjust";
            participantIds = expense.split.entries.map((e) => e.memberId);
            for (const e of expense.split.entries) {
                splitValues[e.memberId] = e.amount === null ? "" : formatAmountForInput(e.amount, currency);
            }
            break;
    }
    return {
        title: expense.title,
        category: expense.category,
        amountText: formatAmountForInput(expense.amount, currency),
        payerId: expense.payers[0]?.memberId ?? null,
        participantIds,
        splitMode,
        splitValues,
        occurredOn: expense.occurredOn,
        note: expense.note ?? "",
    };
}
function formatAmountForInput(minor, currency) {
    if (currency === "JPY" || currency === "KRW" || currency === "TWD")
        return String(minor);
    return (minor / 100).toFixed(2);
}
/** フォームの状態を API へ送る形に変換する */
export function buildExpenseInput(state, currency) {
    const title = state.title.trim();
    if (title === "") {
        return { ok: false, field: "title", message: "支出名を入力してください" };
    }
    const amount = parseAmountInput(state.amountText, currency);
    if (amount === null || amount <= 0) {
        return { ok: false, field: "amountText", message: "金額を正しく入力してください" };
    }
    if (!state.payerId) {
        return { ok: false, field: "payerId", message: "立て替えた人を選んでください" };
    }
    if (state.participantIds.length === 0) {
        return { ok: false, field: "participantIds", message: "負担する人を1人以上選んでください" };
    }
    const split = buildSplitRule(state, currency, amount);
    if ("message" in split) {
        return { ok: false, field: "splitValues", message: split.message };
    }
    return {
        ok: true,
        input: {
            title,
            // カテゴリは選ばせない。候補から選んだときはその値、手入力なら支出名から推測する
            // (一覧の絵文字にしか使わないので、外れても実害が無い)。
            category: state.category === "other" ? inferCategory(title) : state.category,
            amount,
            payers: [{ memberId: state.payerId, amount }],
            split: split.rule,
            occurredOn: state.occurredOn,
            note: state.note.trim() === "" ? null : state.note.trim(),
        },
    };
}
function buildSplitRule(state, currency, amount) {
    switch (state.splitMode) {
        case "equal":
            return { rule: { type: "equal", memberIds: state.participantIds } };
        case "shares": {
            const entries = state.participantIds.map((memberId) => ({
                memberId,
                share: Number(state.splitValues[memberId] ?? "1"),
            }));
            if (entries.some((e) => !Number.isFinite(e.share) || e.share <= 0)) {
                return { message: "比率は 0 より大きい数値で入力してください" };
            }
            return { rule: { type: "shares", entries } };
        }
        case "adjust": {
            // 空欄のままの人は「残額を均等割りする対象」
            const entries = state.participantIds.map((memberId) => {
                const raw = (state.splitValues[memberId] ?? "").trim();
                if (raw === "")
                    return { memberId, amount: null };
                const parsed = parseAmountInput(raw, currency);
                return { memberId, amount: parsed === null || parsed < 0 ? -1 : parsed };
            });
            if (entries.some((e) => e.amount === -1)) {
                return { message: "変更した金額を正しく入力してください" };
            }
            const fixedTotal = entries.reduce((acc, e) => acc + (e.amount ?? 0), 0);
            const flexibleCount = entries.filter((e) => e.amount === null).length;
            if (fixedTotal > amount) {
                return { message: `変更した金額の合計が支出額を超えています (${fixedTotal - amount} 超過)` };
            }
            if (flexibleCount === 0 && fixedTotal !== amount) {
                return { message: `金額の合計が支出額と一致しません (差額 ${amount - fixedTotal})` };
            }
            return { rule: { type: "adjusted", entries } };
        }
    }
}
/**
 * 現在の入力内容から「1人あたりいくらか」を計算する。
 *
 * 金額を入れた直後は均等割りの結果を、誰かの金額を変更したあとは
 * 残りの人に再配分した結果を返す。入力の途中で成立しない場合は null。
 */
export function computeShares(state, currency) {
    const amount = parseAmountInput(state.amountText, currency);
    if (amount === null || amount <= 0)
        return null;
    if (state.participantIds.length === 0)
        return null;
    const split = buildSplitRule(state, currency, amount);
    if ("message" in split)
        return null;
    try {
        return allocate(amount, split.rule);
    }
    catch {
        return null;
    }
}
/**
 * 入力候補を選んだときにフォームへ反映する。
 *
 * 埋めるのはタイトルとカテゴリだけで、金額は埋めない。
 * 金額は毎回違うため、埋めておくと「直し忘れ」のほうが起きやすい。
 */
export function applySuggestion(state, suggestion) {
    return {
        ...state,
        title: suggestion.title,
        category: suggestion.category,
    };
}
/** 参加者の選択をトグルする */
export function toggleParticipant(state, memberId) {
    const selected = state.participantIds.includes(memberId);
    return {
        ...state,
        participantIds: selected
            ? state.participantIds.filter((id) => id !== memberId)
            : [...state.participantIds, memberId],
    };
}
//# sourceMappingURL=expense-form.js.map